import {
  point,
  lineString,
  polygon,
  multiPolygon,
  booleanIntersects,
} from "@turf/turf";

import indexOf from "lodash/indexOf";
import size from "lodash/size";
import isEmpty from "lodash/isEmpty";
import get from "lodash/get";
import last from "lodash/last";
import filter from "lodash/filter";
import cloneDeep from "lodash/cloneDeep";

import {
  FEATURE_TYPES,
  SHOW_LABELS_DEFAULT_ZOOM,
} from "planning/GisMap/layers/common/configuration";
import { LayerKeyMappings } from "planning/GisMap/utils";

// filter gis data utils

export const checkIfFilterActive = (filterState) => {
  if (filterState.status.length) {
    return true;
  }
  // ticket check
  return false;
};

// filterState: planningGis reducer "filters" object
// layerData shape : { layer-key: [ {...Gis data, ...}, ...] }
// blackList : list of layerKeys not to filter
// filter Full master data for each layerKeys
export const filterGisData = (
  filterState,
  layerData,
  whiteList = [],
  blackList = ["region", "ticket"]
) => {
  let filteredData = cloneDeep(layerData);
  // handle status filter first
  const statusFilterList = filterState.status;
  if (!!statusFilterList.length) {
    // get keys of layerData
    const layerKeyList = Object.keys(filteredData);
    // loop over layerKeys
    for (let lkInd = 0; lkInd < layerKeyList.length; lkInd++) {
      const currLayerKey = layerKeyList[lkInd];
      if (blackList.indexOf(currLayerKey) !== -1) continue;
      if (size(whiteList) && whiteList.indexOf(currLayerKey) === -1) continue;
      // filter list of elements of each layer key
      filteredData[currLayerKey] = filter(
        filteredData[currLayerKey],
        (d) => statusFilterList.indexOf(d.status) !== -1
      );
    }
  }

  return filteredData;
};

// filterState: planningGis reducer "filters" object
// layerData shape : [ {...Gis data, ...}, ...]
// filter gis data of only single layer or gis list data
export const filterGisLayerData = (filterState, layerData) => {
  let filteredData = cloneDeep(layerData);
  // handle status filter first
  const statusFilterList = filterState.status;
  if (!!statusFilterList.length) {
    // filter list of elements of each layer key
    filteredData = filter(
      filteredData,
      (d) => statusFilterList.indexOf(d.status) !== -1
    );
  }

  return filteredData;
};

export const filterGisDataByPolygon = ({
  filterPolygon,
  gisData,
  whiteList = [],
  blackList = [],
  groupByLayerKey = false,
}) => {
  const elementResultList = [];
  // user this if groupByLayerKey true, will return same data as input gisData but filtered
  const elementGroupResult = {};
  // loop over layerData
  const layerKeyList = Object.keys(gisData);
  // check intersects
  for (let lkInd = 0; lkInd < layerKeyList.length; lkInd++) {
    const currLayerKey = layerKeyList[lkInd];
    elementGroupResult[currLayerKey] = [];
    // don't filter if not in whitelist
    if (size(whiteList) && indexOf(whiteList, currLayerKey) === -1) continue;
    // don't filter if in blackList
    if (size(blackList) && indexOf(blackList, currLayerKey) !== -1) continue;
    const currLayerData = gisData[currLayerKey];
    const featureType = LayerKeyMappings[currLayerKey]["featureType"];

    for (let elemInd = 0; elemInd < currLayerData.length; elemInd++) {
      const element = currLayerData[elemInd];
      // create turf geom for each element
      let turfGeom;
      if (featureType === FEATURE_TYPES.POINT) {
        turfGeom = point(element.geometry);
      } else if (featureType === FEATURE_TYPES.POLYLINE) {
        turfGeom = lineString(element.geometry);
      } else if (featureType === FEATURE_TYPES.POLYGON) {
        turfGeom = polygon([element.geometry]);
      } else {
        // multi polygon
        turfGeom = multiPolygon(element.coordinates);
      }
      // check intersects
      const isIntersecting = booleanIntersects(filterPolygon, turfGeom);
      // add to list if intersect true
      if (isIntersecting) {
        // generate {layerKey: [...filteredElements], ...} shape of data
        if (groupByLayerKey) {
          elementGroupResult[currLayerKey].push({
            ...element,
            layerKey: currLayerKey,
          });
        } else {
          // simple list of elements : [...filteredElements]
          elementResultList.push({
            ...element,
            layerKey: currLayerKey,
          });
        }
      }
    }
  }
  return groupByLayerKey ? elementGroupResult : elementResultList;
};

export const updateLayerDataShowLabel = ({
  layerKey,
  elementList,
  currentZoom,
  coordinates,
}) => {
  const featureType = LayerKeyMappings[layerKey]["featureType"];
  const layerShowLabelZoom =
    LayerKeyMappings[layerKey]["showLabelZoom"] || SHOW_LABELS_DEFAULT_ZOOM;

  if (currentZoom >= layerShowLabelZoom) {
    // create turf polygon
    const tf_boundPolygon = polygon(coordinates);

    for (let elemInd = 0; elemInd < elementList.length; elemInd++) {
      const element = elementList[elemInd];
      // create turf geom for each element
      let turfGeom;
      if (featureType === FEATURE_TYPES.POINT) {
        turfGeom = point(element.geometry);
      } else if (featureType === FEATURE_TYPES.POLYLINE) {
        turfGeom = lineString(element.geometry);
      } else if (featureType === FEATURE_TYPES.POLYGON) {
        turfGeom = polygon([element.geometry]);
      } else {
        // multi polygon
        turfGeom = multiPolygon(element.coordinates);
      }
      // check intersects
      const isIntersecting = booleanIntersects(tf_boundPolygon, turfGeom);
      element.showLabel = isIntersecting;
    }
  } else {
    // clear all element show zoom
    for (let elemInd = 0; elemInd < elementList.length; elemInd++) {
      const element = elementList[elemInd];
      element.showLabel = false;
    }
  }
};

// get list of layer keys that have label enabled from layerNetworkState and selected
export const getActiveLayerLabelKeys = (layerNetworkState) => {
  const layerKeysWithLabel = [];
  for (const layerKey in layerNetworkState) {
    if (Object.hasOwnProperty.call(layerNetworkState, layerKey)) {
      const networkState = layerNetworkState[layerKey];
      if (networkState?.isSelected && networkState?.showLabel) {
        layerKeysWithLabel.push(layerKey);
      }
    }
  }
  return layerKeysWithLabel;
};

/**
 * generate network_id using parent data and region list
 * parents : { layerKey : [{id, name, uid, netid}, ... ], ...]
 */
export const generateNetworkIdFromParent = (uniqueId, parents, regionList) => {
  let networkId = "";
  if (isEmpty(parents)) {
    // get region uid
    const reg_uid = !!size(regionList) ? last(regionList).unique_id : "RGN";
    networkId = `${reg_uid}-${uniqueId}`;
  } else {
    // generate network id from parent list, get first key
    const firstLayerKey = Object.keys(parents)[0];
    const parentNetId = get(parents, [firstLayerKey, "0", "network_id"], "PNI");
    networkId = `${parentNetId}-${uniqueId}`;
  }
  return networkId;
};
